import { AssignmentOutlined, AutoAwesome, InfoOutlined } from "@mui/icons-material";
import { useMemo, useRef, useState } from "react";
import intl from "src/internationalization/intl";
import { Button, CircularProgress, Divider, Grid, IconWrapper, InnerSection, InvestorProfileChip, Stack, Typography } from "src/lib";
import { Accordion } from "src/lib/components/composition";
import { formatCurrencyBRL, mapPerfilToProfile } from "src/lib/transformers/ClientsOverviewInfo";
import { getResponseFromQuestion, runMessageSuggestion } from "src/services/api/chat";
import {
    GenerateRecommendationProductsEvent,
    MessageSuggestionContext,
    MessageSuggestionSelectedProduct,
    RecommendationScenario,
} from "src/services/api/commitments/types";
import { generateRecommendationProducts, mergeRankingTexts, normalizeRecommendationResponse } from "src/services/api/commitments";
import { useCashRecommendationStore } from "src/store/cashRecommendationStore";
import { getStoreKey } from "src/store/cashRecommendationStore/types";
import { useDrawerStore } from "src/store/drawerStore";
import { useBatchedStreamCallbacks } from "src/hooks/useBatchedStreamCallbacks";
import { useMessageStore } from "src/store/messageStore";
import { useModalStore } from "src/store/modalStore";
import { useCommitmentAccordeonStore } from "src/store";
import { useConfirmedRankingStore } from "src/store/confirmedRankingStore";
import { IDetailsProps } from "./types";
import { alpha, Box, Dialog, DialogContent, DialogTitle, IconButton, Radio, Tooltip } from "@mui/material";

/** Ensures a value is rendered as a string; backend may send date objects (e.g. _Date__year/month/day). */
function safeDisplay(value: unknown): string {
    if (value == null || value === "") return "-";
    if (typeof value === "string" || typeof value === "number") return String(value);
    if (typeof value === "object" && value !== null && "_Date__year" in value) {
        const d = value as { _Date__year: number; _Date__month: number; _Date__day: number };
        const y = d._Date__year ?? 0;
        const m = String(d._Date__month ?? 0).padStart(2, "0");
        const day = String(d._Date__day ?? 0).padStart(2, "0");
        return `${y}-${m}-${day}`;
    }
    return String(value);
}

/** Builds the 2 dynamic column slots (col_1/val_1, col_2/val_2); uses "-" for null. */
const getDynamicColumns = (product: Record<string, unknown>) => [
    { title: safeDisplay(product.col_1), value: safeDisplay(product.val_1) },
    { title: safeDisplay(product.col_2), value: safeDisplay(product.val_2) },
];


const mapObjectToItems = (obj: Record<string, unknown>, keyPrefix = ""): Array<{ key: string; value: string }> =>
    Object.entries(obj).flatMap(([key, value]) => {
        const itemKey = keyPrefix ? `${keyPrefix}.${key}` : key;
        if (value == null || value === "") return [];
        if (Array.isArray(value)) {
            if (value.length === 0) return [];
            return [{ key: itemKey, value: JSON.stringify(value) }];
        }
        if (typeof value === "object") {
            return mapObjectToItems(value as Record<string, unknown>, itemKey);
        }
        return [{ key: itemKey, value: safeDisplay(value) }];
    });

const parseQaResponseToItems = (responseJson: string): Array<{ key: string; value: string }> => {
    try {
        const parsed = JSON.parse(responseJson);
        if (Array.isArray(parsed)) {
            return parsed.flatMap((item, idx) => {
                if (item && typeof item === "object") {
                    const obj = item as Record<string, unknown>;
                    if (obj.p && typeof obj.p === "object") {
                        const fromProduct = mapObjectToItems(obj.p as Record<string, unknown>, `item_${idx + 1}`);
                        const extra = mapObjectToItems(
                            Object.fromEntries(Object.entries(obj).filter(([key]) => key !== "p")),
                            `item_${idx + 1}`
                        );
                        return [...fromProduct, ...extra].filter((entry) => entry.value !== "-");
                    }
                    return mapObjectToItems(obj, `item_${idx + 1}`).filter((entry) => entry.value !== "-");
                }
                return [{ key: `item_${idx + 1}`, value: safeDisplay(item) }].filter((entry) => entry.value !== "-");
            });
        }

        if (parsed && typeof parsed === "object") {
            const parsedObj = parsed as Record<string, unknown>;
            // Common shape from run_qa: { p: { ...productFields }, documents: [...] }
            if (parsedObj.p && typeof parsedObj.p === "object") {
                const fromProduct = mapObjectToItems(parsedObj.p as Record<string, unknown>, "");
                const extra = mapObjectToItems(
                    Object.fromEntries(Object.entries(parsedObj).filter(([key]) => key !== "p")),
                    ""
                );
                return [...fromProduct, ...extra].filter((item) => item.value !== "-");
            }
            return mapObjectToItems(parsedObj, "").filter((item) => item.value !== "-");
        }
    } catch {
        // fallback below for non-json responses
    }

    return responseJson
        ? [{ key: "detalhes", value: responseJson }]
        : [];
};

const formatPropertyLabel = (key: string): string => key.replace(/^item_\d+\./, "");

const DEFAULT_RANKINGS = {
    fund_products_query: "MATCH (is:InvestmentStrategy)<-[:HAS_STRATEGY]-(p:Product)-[:IS_ON_MARKET]->(m:Market {idMarket : \"FUNDOS\"})\nORDER BY p.minimumInitialInvestment DESC\nWITH is, properties(p) as p\nRETURN is, collect(p)[..10] as products",
    fixed_income_products_query: "MATCH (is:InvestmentStrategy)<-[:HAS_STRATEGY]-(p:Product)-[:IS_ON_MARKET]->(m:Market {idMarket : \"RENDA FIXA\"})\nWHERE p.dateDue is not null\nORDER BY p.dateDue DESC\nWITH is, properties(p) as p\nRETURN is, collect(p)[..10] as products",
    retirement_products_query: "MATCH (is:InvestmentStrategy)<-[:HAS_STRATEGY]-(p:Product)-[:IS_ON_MARKET]->(m:Market {idMarket : \"PREVIDENCIA\"})\nORDER BY p.minimumInitialInvestment DESC\nWITH is, properties(p) as p\nRETURN is, collect(p)[..10] as products",
    variable_income_products_query: "",
    crypto_products_query: "",
    coe_products_query: "",
};

type PropertiesCacheValue = {
    items: Array<{ key: string; value: string }>
    error: string | null
}

const Details = ({ client, accordionId, variant = "commitment", opportunities = [], ...props }: IDetailsProps) => {
    const isOpportunity = variant === "opportunity"
    const confirmedRankingByMarket = useConfirmedRankingStore((s) => s.confirmedRankingByMarket)
    const confirmedFundos = useConfirmedRankingStore((s) => s.getConfirmedRanking("FUNDOS"))
    const confirmedPrevidencia = useConfirmedRankingStore((s) => s.getConfirmedRanking("PREVIDENCIA"))
    const confirmedRendaFixa = useConfirmedRankingStore((s) => s.getConfirmedRanking("RENDA FIXA"))
    const confirmedRendaVariavel = useConfirmedRankingStore((s) => s.getConfirmedRanking("RENDA VARIAVEL"))
    const rankings = useMemo(() => {
        const base = {
            ...DEFAULT_RANKINGS,
            fund_products_query: confirmedFundos?.cypher_query ?? DEFAULT_RANKINGS.fund_products_query,
            fixed_income_products_query:
                confirmedRendaFixa?.cypher_query ?? DEFAULT_RANKINGS.fixed_income_products_query,
            retirement_products_query:
                confirmedPrevidencia?.cypher_query ?? DEFAULT_RANKINGS.retirement_products_query,
            variable_income_products_query:
                confirmedRendaVariavel?.cypher_query ?? DEFAULT_RANKINGS.variable_income_products_query,
        }
        return mergeRankingTexts(base, confirmedRankingByMarket)
    }, [
        confirmedFundos?.cypher_query,
        confirmedRendaFixa?.cypher_query,
        confirmedPrevidencia?.cypher_query,
        confirmedRendaVariavel?.cypher_query,
        confirmedRankingByMarket,
    ])
    const setOpen = useModalStore((s: any) => s.setOpen)
    const openDrawer = useDrawerStore((s: any) => s.openDrawer)
    const addMessage = useMessageStore((s: any) => s.addMessage)
    const updateMessage = useMessageStore((s: any) => s.updateMessage)
    const isStreaming = useMessageStore((s: any) => s.isStreaming)
    const setIsStreaming = useMessageStore((s: any) => s.setIsStreaming)
    const { createCallbacks, flushPending } = useBatchedStreamCallbacks()
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [reloadCount, setReloadCount] = useState(0)
    const [propertiesModalOpen, setPropertiesModalOpen] = useState(false)
    const [propertiesModalTitle, setPropertiesModalTitle] = useState("")
    const [propertiesModalItems, setPropertiesModalItems] = useState<Array<{ key: string; value: string }>>([])
    const [propertiesModalError, setPropertiesModalError] = useState<string | null>(null)
    const [propertiesModalLoading, setPropertiesModalLoading] = useState(false)
    const propertiesByProductCacheRef = useRef<Map<string, PropertiesCacheValue>>(new Map())
    const setLoading = useCashRecommendationStore((s) => s.setLoading)
    const setResult = useCashRecommendationStore((s) => s.setResult)
    const setError = useCashRecommendationStore((s) => s.setError)
    const accordions = useCommitmentAccordeonStore((s: any) => s.accordions)
    const isRecommendationOpen = useCommitmentAccordeonStore((s: any) => s.isRecommendationOpen)
    const toggleRecommendation = useCommitmentAccordeonStore((s: any) => s.toggleRecommendation)
    const setRecommendations = useCommitmentAccordeonStore((s: any) => s.setRecommendations)

    const account = client?.client?.account ?? null
    const accountStr = account != null ? String(account) : null
    const cashEntry = useCashRecommendationStore((s) => (accountStr != null ? s.byKey[getStoreKey(accountStr, "cash")] : undefined))
    const commitmentEntry = useCashRecommendationStore((s) => (accountStr != null ? s.byKey[getStoreKey(accountStr, "commitment")] : undefined))
    const [selectedOpportunityKey, setSelectedOpportunityKey] = useState<string | null>(null)
    const opportunityEntry = useCashRecommendationStore((s) =>
        accountStr != null && selectedOpportunityKey != null
            ? s.byKey[getStoreKey(accountStr, "opportunity", selectedOpportunityKey)]
            : undefined
    )
    const isCashOpportunity = client?.is_cash_opportunity && !isOpportunity
    const isCommitmentEvent = !isCashOpportunity && !isOpportunity
    const clientPerfil = client?.client?.perfil

    const activeRecommendationEntry =
        isOpportunity ? opportunityEntry : isCashOpportunity ? cashEntry : commitmentEntry
    const normalizedRecommendations = useMemo(
        () => normalizeRecommendationResponse(activeRecommendationEntry?.data),
        [activeRecommendationEntry?.data],
    )
    const recommendationProducts = normalizedRecommendations.list_of_products
    const recommendationScenario = normalizedRecommendations.scenario

    const getJustification = (product: any): string | null => {
        const text = typeof product?.justification === "string" ? product.justification.trim() : ""
        return text.length > 0 ? text : null
    }

    const renderField = (label: string, value: unknown) => {
        const raw = safeDisplay(value)
        const text = raw === "-" ? "—" : raw
        const muted = text === "—"
        return (
            <Grid item xs={6} sx={{ minWidth: 0 }}>
                <Typography variant="caption" color="quaternary" sx={{ display: "block" }}>{label}</Typography>
                <Typography
                    variant="body2"
                    sx={{
                        fontWeight: 500,
                        color: muted ? "neutral.dark" : undefined,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                    }}
                >
                    {text}
                </Typography>
            </Grid>
        )
    }

    const renderProductCard = (product: any, idx: number) => {
        const justification = getJustification(product)
        const investmentLabel = `${intl("investment")} ${idx + 1}`
        const dynamicCols = getDynamicColumns(product).filter((c) => c.title && c.title !== "-")
        const isSelected = isRecommendationOpen(accordionId, String(idx))
        return (
            <Box
                key={`product-${idx}`}
                onClick={() => toggleRecommendation(accordionId, String(idx))}
                sx={(theme) => ({
                    border: `2px solid ${isSelected ? theme.palette.primary.main : (theme.palette as any).neutral.darkest}`,
                    borderRadius: 1,
                    bgcolor: isSelected ? alpha(theme.palette.primary.main, 0.1) : (theme.palette as any).neutral.darkest,
                    p: 2,
                    minWidth: 0,
                    display: "flex",
                    flexDirection: "column",
                    gap: 1.5,
                    cursor: "pointer",
                    transition: theme.transitions.create(["border-color", "background-color"], {
                        duration: theme.transitions.duration.short,
                    }),
                })}
            >
                {/* Cabeçalho: nome + propriedades */}
                <Grid container wrap="nowrap" alignItems="flex-start" gap={1} sx={{ minWidth: 0 }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="caption" color="quaternary" sx={{ display: "block" }}>
                            {investmentLabel}
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 700, wordBreak: "break-word" }}>
                            {product.product}
                        </Typography>
                    </Box>
                    <Tooltip title="Ver informações do produto" arrow placement="top">
                        <IconButton
                            color="secondary"
                            size="small"
                            sx={{ flexShrink: 0 }}
                            onClick={(e) => {
                                e.stopPropagation()
                                handleOpenPropertiesPopup(investmentLabel, product)
                            }}
                        >
                            <InfoOutlined fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Grid>

                {/* Campos rotulados (labels sempre visíveis, valor vazio = "—") */}
                <Grid container spacing={1}>
                    {renderField("Mercado", product.market)}
                    {renderField("Estratégia", product.strategy ?? product.category)}
                    {renderField(intl("category"), product.category)}
                    <Grid item xs={6} sx={{ minWidth: 0 }}>
                        <Typography variant="caption" color="quaternary" sx={{ display: "block" }}>
                            {intl("suitability")}
                        </Typography>
                        <Box sx={{ mt: 0.5 }}>
                            <InvestorProfileChip profile={mapPerfilToProfile(product.suitability || clientPerfil)} />
                        </Box>
                    </Grid>
                    {dynamicCols.map((col, i) => (
                        <Grid item xs={6} key={`dyn-${i}`} sx={{ minWidth: 0 }}>
                            <Typography variant="caption" color="quaternary" sx={{ display: "block" }}>{col.title}</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis" }}>
                                {col.value}
                            </Typography>
                        </Grid>
                    ))}
                </Grid>

                <Divider color="secondary" />

                {/* Motivo da recomendação */}
                <Box>
                    <Typography variant="caption" color="quaternary" sx={{ display: "block" }}>
                        {intl("recommendationReason")}
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{ color: justification ? undefined : "neutral.dark", lineHeight: 1.4 }}
                    >
                        {justification ?? "—"}
                    </Typography>
                </Box>
            </Box>
        )
    }

    const renderProductsGrid = (products: any[]) => (
        <Box
            sx={{
                mt: 2,
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
                gap: 1.5,
            }}
        >
            {products.map((product, idx) => renderProductCard(product, idx))}
        </Box>
    )

    const suggestionsTitle = intl("invSugestions") || "Sugestões de investimento"
    const lastRender = useMemo(
        () => new Date().toLocaleString("pt-BR"),
        // reloadCount invalidates the displayed timestamp after manual refresh
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [reloadCount]
    )
    const selectedRecommendationIds = useMemo(() => {
        const entry = accordions.find((item: any) => item.id === accordionId)
        return new Set((entry?.recommendations ?? []).map((rec: any) => rec.id))
    }, [accordions, accordionId])

    const handleOpenRelatedOpportunities = () => {
        setOpen("relatedOpportunitiesModal", client.client)
    }

    const handleRefreshSuggestions = () => {
        if (accountStr == null) return
        setReloadCount((prev) => prev + 1)
        if (isCashOpportunity) {
            const payload = {
                client: accountStr,
                opportunity: "",
                event: { cliente_com_caixa: { net_worth: Number(client?.net_worth ?? 0) } },
                rankings,
                associated_opportunities: [],
            }
            setLoading(accountStr, "cash")
            generateRecommendationProducts(payload as any)
                .then((response) => setResult(accountStr, "cash", response))
                .catch((err: any) => setError(accountStr, "cash", err?.message ?? "Erro ao carregar recomendações."))
            return
        }
        if (isCommitmentEvent) {
            const payload = {
                client: accountStr,
                opportunity: "",
                event: {
                    produto_a_vencer: {
                        commitmentId: client?.produto_a_vencer?.commitmentId ?? "",
                        nameProduct: client?.produto_a_vencer?.nameProduct ?? "",
                    },
                },
                rankings,
                associated_opportunities: [],
            }
            setLoading(accountStr, "commitment")
            generateRecommendationProducts(payload as any)
                .then((response) => setResult(accountStr, "commitment", response))
                .catch((err: any) => setError(accountStr, "commitment", err?.message ?? "Erro ao carregar recomendações."))
            return
        }
        if (isOpportunity && selectedOpportunityKey != null) {
            const opportunity = opportunities.find((o: any, i: number) => String(o?.objective_id ?? i) === selectedOpportunityKey)
            const objectiveId = String(opportunity?.objective_id ?? selectedOpportunityKey)
            const payload = {
                client: accountStr,
                opportunity: opportunity?.text ?? "",
                event: { oportunidade: {} },
                rankings,
                associated_opportunities: [objectiveId],
            }
            setLoading(accountStr, "opportunity", selectedOpportunityKey)
            generateRecommendationProducts(payload as any)
                .then((response) => setResult(accountStr, "opportunity", response, selectedOpportunityKey))
                .catch((err: any) => setError(accountStr, "opportunity", err?.message ?? "Erro ao carregar recomendações.", selectedOpportunityKey))
        }
    }

    const mapProductToSelectedProduct = (p: any): MessageSuggestionSelectedProduct => ({
        product: p.product ?? "",
        market: p.market,
        strategy: p.strategy,
        category: p.category ?? "",
        justification: typeof p.justification === "string" && p.justification.trim() ? p.justification.trim() : undefined,
        suitability: p.suitability,
        col_1: p.col_1 ?? null,
        val_1: p.val_1 ?? null,
        col_2: p.col_2 ?? null,
        val_2: p.val_2 ?? null,
    })

    const buildMessageSuggestionContext = (
        selectedList: any[],
        scenario?: RecommendationScenario,
    ): MessageSuggestionContext | null => {
        if (accountStr == null || selectedList.length === 0) return null

        let event: GenerateRecommendationProductsEvent
        let opportunity = ""
        let associatedOpportunities: string[] = []

        if (isCashOpportunity) {
            event = { cliente_com_caixa: { net_worth: Number(client?.net_worth ?? 0) } }
        } else if (isCommitmentEvent) {
            event = {
                produto_a_vencer: {
                    commitmentId: client?.produto_a_vencer?.commitmentId ?? "",
                    nameProduct: client?.produto_a_vencer?.nameProduct ?? "",
                },
            }
        } else if (isOpportunity && selectedOpportunityKey != null) {
            const opportunityItem = opportunities.find(
                (o: any, i: number) => String(o?.objective_id ?? i) === selectedOpportunityKey,
            )
            const objectiveId = String(opportunityItem?.objective_id ?? selectedOpportunityKey)
            event = { oportunidade: {} }
            opportunity = opportunityItem?.text ?? ""
            associatedOpportunities = [objectiveId]
        } else {
            event = { oportunidade: {} }
        }

        const base: MessageSuggestionContext = {
            client: {
                account: accountStr,
                name: client?.client?.name,
                perfil: client?.client?.perfil,
            },
            scenario,
            event,
            opportunity,
            associated_opportunities: associatedOpportunities,
            allocation_details: client?.allocation_details,
            selected_products: selectedList.map(mapProductToSelectedProduct),
        }

        if (client?.source === "open_finance" && client?.finantialInstitution) {
            base.open_finance_context = {
                finantialInstitution: client.finantialInstitution,
                ...(typeof client?.net_amount === "number"
                    ? { financial_impact: client.net_amount }
                    : {}),
            }
        }

        return base
    }

    const handleOpenMessageSuggestion = async () => {
        const selectedList = recommendationProducts.filter((_: any, idx: number) =>
            selectedRecommendationIds.has(String(idx)),
        )
        const messageContext = buildMessageSuggestionContext(selectedList, recommendationScenario as RecommendationScenario | undefined)
        if (!messageContext || isStreaming) return

        openDrawer()
        setIsStreaming(true)
        const assistantId = addMessage({
            text: "",
            timestamp: new Date(),
            answer: true,
            status: "streaming",
            streamStatus: intl("chatStreamingStarting"),
        })

        try {
            setIsLoading(true)
            const response = await runMessageSuggestion(messageContext, createCallbacks(assistantId))

            flushPending(assistantId)
            updateMessage(assistantId, {
                text: response?.natural_language_response || "",
                status: "done",
                streamStatus: undefined,
                jsonMessage: response,
            })
        } catch (err: any) {
            console.error("Erro ao buscar sugestão de mensagem:", err)
            flushPending(assistantId)
            const partial =
                useMessageStore.getState().messages.find((m: any) => m.id === assistantId)?.text || ""
            const errMsg = err.message || "Erro ao gerar sugestão de mensagem. Tente novamente."
            updateMessage(assistantId, {
                text: partial ? `${partial}\n\n${errMsg}` : errMsg,
                status: "error",
                streamStatus: undefined,
            })
        } finally {
            setIsLoading(false)
            setIsStreaming(false)
        }
    }

    const handleOpenPropertiesPopup = async (label: string, product: Record<string, unknown>) => {
        console.log("[Details] handleOpenPropertiesPopup:start", { label, product })
        setPropertiesModalTitle(label)
        setPropertiesModalItems([])
        setPropertiesModalError(null)
        setPropertiesModalOpen(true)
        try {
            if (!product || typeof product !== "object") throw new Error("invalid-product")
            const productName = safeDisplay(product.product)
            if (!productName || productName === "-") throw new Error("missing-product-name")
            const cacheKey = `${accountStr ?? "no-account"}::${productName}`
            const cached = propertiesByProductCacheRef.current.get(cacheKey)
            if (cached) {
                setPropertiesModalItems(cached.items)
                setPropertiesModalError(cached.error)
                console.log("[Details] handleOpenPropertiesPopup:cache-hit", { cacheKey, itemsCount: cached.items.length })
                return
            }

            setPropertiesModalLoading(true)
            const question = `Me retorne todas as informacoes do produto ${productName}`
            const qaResponse = await getResponseFromQuestion({ question })
            const items = parseQaResponseToItems(qaResponse?.response_json ?? "")
            const errorMessage =
                items.length === 0
                    ? "Não encontramos valores dessas propriedades neste produto de recomendação."
                    : null
            console.log("[Details] run_qa resolved items", {
                productName,
                itemsCount: items.length,
                firstTen: items.slice(0, 10),
            })

            setPropertiesModalTitle(label)
            setPropertiesModalItems(items)
            setPropertiesModalError(errorMessage)
            propertiesByProductCacheRef.current.set(cacheKey, { items, error: errorMessage })
            console.log("[Details] handleOpenPropertiesPopup:success", { label, itemsCount: items.length })
        } catch (error) {
            console.error("[Details] handleOpenPropertiesPopup:error", {
                label,
                error,
                product,
            })
            setPropertiesModalItems([])
            setPropertiesModalError("Não foi possível carregar as propriedades.")
        } finally {
            setPropertiesModalLoading(false)
        }
    }

    const propertiesPopup = (
        <Dialog open={propertiesModalOpen} onClose={() => setPropertiesModalOpen(false)} fullWidth maxWidth="sm">
            <DialogTitle>
                <Typography variant="h4" sx={{ color: "#fff" }}>
                    {propertiesModalTitle || "Propriedades do produto"}
                </Typography>
            </DialogTitle>
            <DialogContent>
                {propertiesModalLoading ? (
                    <Grid container justifyContent="center" sx={{ py: 2 }}>
                        <CircularProgress color="primary" size={24} />
                    </Grid>
                ) : propertiesModalError ? (
                    <Typography variant="body2" sx={{ color: "#fff" }}>{propertiesModalError}</Typography>
                ) : (
                    <Grid container direction="column" gap={1}>
                        {propertiesModalItems.map((item) => (
                            <Grid key={item.key} container justifyContent="space-between" gap={1}>
                                <Typography variant="caption" sx={{ color: "#fff" }}>{formatPropertyLabel(item.key)}</Typography>
                                <Typography variant="body2" sx={{ color: "#fff", textAlign: "right", wordBreak: "break-word" }}>{item.value}</Typography>
                            </Grid>
                        ))}
                    </Grid>
                )}
            </DialogContent>
        </Dialog>
    )

    if (isOpportunity) {
        return (
            <Accordion.Details {...props}>
                <Stack sx={{ mt: 2 }}>
                    {opportunities.map((opportunity: any, idx: number) => {
                        const key = String(opportunity?.objective_id ?? idx)
                        const isSelected = selectedOpportunityKey === key
                        return (
                            <Grid key={`opportunity-item-${key}`} sx={{ mt: 2 }}>
                                <Grid container alignItems="center" gap={4}>
                                    <Grid>
                                        <Grid container gap={1} alignItems="center">
                                            <Radio
                                                checked={isSelected}
                                                onChange={() => {
                                                    if (accountStr == null) return
                                                    setSelectedOpportunityKey(key)
                                                    setRecommendations(accordionId, [])
                                                    const payload = {
                                                        client: accountStr,
                                                        opportunity: opportunity?.text ?? "",
                                                        event: { oportunidade: {} },
                                                        rankings,
                                                        associated_opportunities: [key],
                                                    }
                                                    setLoading(accountStr, "opportunity", key)
                                                    generateRecommendationProducts(payload as any)
                                                        .then((response: any) => setResult(accountStr, "opportunity", response, key))
                                                        .catch((err: any) => setError(accountStr, "opportunity", err?.message ?? "Erro ao carregar recomendações.", key))
                                                }}
                                            />
                                            <Typography>
                                                {opportunity?.title ?? `Oportunidade ${idx + 1}`}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                    <Grid sx={{ width: 320, minWidth: 320 }}>
                                        <InnerSection title="Resumo">
                                            <Tooltip title={opportunity?.text || "-"} arrow placement="top">
                                                <span style={{ display: "block" }}>
                                                    <Typography
                                                        variant="body1"
                                                        sx={{
                                                            overflow: "hidden",
                                                            textOverflow: "ellipsis",
                                                            whiteSpace: "nowrap",
                                                        }}
                                                    >
                                                        {opportunity?.text || "-"}
                                                    </Typography>
                                                </span>
                                            </Tooltip>
                                        </InnerSection>
                                    </Grid>
                                    <Grid>
                                        <InnerSection title="Valor da oportunidade">
                                            <Typography variant="body1">
                                                {formatCurrencyBRL(opportunity?.volumeOportunidade ?? 0)}
                                            </Typography>
                                        </InnerSection>
                                    </Grid>
                                </Grid>
                                <Divider color="secondary" sx={{ mt: 2 }} />
                            </Grid>
                        )
                    })}
                </Stack>
                <Typography variant="caption" sx={{ mt: 3, display: "block", ml: 3 }}>
                    Sugestões de investimento para a oportunidade selecionada
                </Typography>
                <Grid
                    container
                    justifyContent="space-between"
                    sx={{ mt: 2, px: 2 }}
                >
                    <Typography variant="caption">
                        {suggestionsTitle}
                    </Typography>
                    <Grid container gap={2} justifyContent="flex-end" alignItems="center">
                        <Tooltip title={intl("recalcTooltip")} arrow placement="top">
                            <Button
                                variant="outlined"
                                color="secondary"
                                onClick={handleRefreshSuggestions}
                                startIcon={<AutoAwesome sx={{ fontSize: 16 }} />}
                            >
                                {intl("recalcRecommendations")}
                            </Button>
                        </Tooltip>
                        <Typography variant="caption">
                            {`Última atualização: ${lastRender}`}
                        </Typography>
                    </Grid>
                </Grid>
                <Stack sx={{ mt: 2, px: 2, pb: 1 }}>
                    {opportunityEntry?.loading && (
                        <Grid container justifyContent="center" sx={{ py: 3 }}>
                            <CircularProgress color="primary" />
                        </Grid>
                    )}
                    {opportunityEntry?.error && !opportunityEntry?.loading && (
                        <Typography variant="body2" sx={{ py: 2, color: (theme: any) => theme.palette?.accentError?.main ?? theme.palette?.error?.main ?? '#d32f2f' }}>
                            {opportunityEntry.error}
                        </Typography>
                    )}
                    {!opportunityEntry?.loading && renderProductsGrid(recommendationProducts)}
                </Stack>
                <Grid
                    container
                    sx={{ width: "100%" }}
                    justifyContent="flex-end"
                >
                    <Button
                        variant="contained"
                        color="primary"
                        sx={{ mt: 2 }}
                        disabled={selectedRecommendationIds.size === 0 || isLoading}
                        onClick={handleOpenMessageSuggestion}
                    >
                        <Grid container gap={2}>
                            <IconWrapper color="neutral">
                                <AutoAwesome />
                            </IconWrapper>
                            <Typography>
                                {intl('sugestInvestimentMsg')}
                            </Typography>
                        </Grid>
                    </Button>
                </Grid>
                {propertiesPopup}
            </Accordion.Details>
        )
    }

    return (
        <Accordion.Details {...props}>
            <Grid
                container
                justifyContent="space-between"
                sx={{ mt: 3 }}
            >
                <Typography variant="caption">
                    {suggestionsTitle}
                </Typography>
                <Grid>
                    <Grid container gap={2} alignItems="center">
                        <Tooltip title={intl("recalcTooltip")} arrow placement="top">
                            <Button
                                variant="outlined"
                                color="secondary"
                                onClick={handleRefreshSuggestions}
                                startIcon={<AutoAwesome sx={{ fontSize: 16 }} />}
                            >
                                {intl("recalcRecommendations")}
                            </Button>
                        </Tooltip>
                        <Typography variant="caption">
                            {`Última atualização: ${lastRender}`}
                        </Typography>
                        {client?.opportunitiesCount > 0 && (
                            <Button
                                variant="outlined"
                                color="neutral"
                                onClick={handleOpenRelatedOpportunities}
                            >
                                <Grid
                                    container
                                    gap={1}
                                    alignItems="center"
                                >
                                    <IconWrapper color="neutral">
                                        <AssignmentOutlined />
                                    </IconWrapper>
                                    <Typography variant="caption">
                                        {`${client.opportunitiesCount} ${client.opportunitiesCount > 1 ? intl('opportunitiesRelatedTo') : intl('opportunityRelatedTo')} ${client.client.name}`}
                                    </Typography>
                                </Grid>
                            </Button>
                        )}
                    </Grid>
                </Grid>
            </Grid>
            <Stack sx={{ mt: 2 }}>
                {isCommitmentEvent && commitmentEntry?.loading && (
                    <Grid container justifyContent="center" sx={{ py: 3 }}>
                        <CircularProgress color="primary" />
                    </Grid>
                )}
                {isCommitmentEvent && commitmentEntry?.error && !commitmentEntry?.loading && (
                    <Typography variant="body2" sx={{ py: 2, color: (theme: any) => theme.palette?.accentError?.main ?? theme.palette?.error?.main ?? '#d32f2f' }}>
                        {commitmentEntry.error}
                    </Typography>
                )}
                {isCommitmentEvent &&
                    !commitmentEntry?.loading &&
                    !commitmentEntry?.error &&
                    recommendationProducts.length === 0 && (
                        <Typography variant="body2" sx={{ py: 2, color: "secondary" }}>
                            Nenhum produto recomendado. Tente atualizar as sugestões.
                        </Typography>
                    )}
                {isCommitmentEvent &&
                    !commitmentEntry?.loading &&
                    recommendationProducts.length > 0 &&
                    renderProductsGrid(recommendationProducts)}
                {isCashOpportunity && cashEntry?.loading && (
                    <Grid container justifyContent="center" sx={{ py: 3 }}>
                        <CircularProgress color="primary" />
                    </Grid>
                )}
                {isCashOpportunity && cashEntry?.error && !cashEntry?.loading && (
                    <Typography variant="body2" sx={{ py: 2, color: (theme: any) => theme.palette?.accentError?.main ?? theme.palette?.error?.main ?? '#d32f2f' }}>
                        {cashEntry.error}
                    </Typography>
                )}
                {isCashOpportunity &&
                    !cashEntry?.loading &&
                    !cashEntry?.error &&
                    recommendationProducts.length === 0 && (
                        <Typography variant="body2" sx={{ py: 2, color: "secondary" }}>
                            Nenhum produto recomendado. Tente atualizar as sugestões.
                        </Typography>
                    )}
                {isCashOpportunity &&
                    !cashEntry?.loading &&
                    recommendationProducts.length > 0 &&
                    renderProductsGrid(recommendationProducts)}
            </Stack>
            <Grid
                container
                sx={{ width: "100%" }}
                justifyContent="flex-end"
            >
                <Button
                    variant="contained"
                    color="primary"
                    sx={{ mt: 2 }}
                    disabled={selectedRecommendationIds.size === 0 || isLoading}
                    onClick={handleOpenMessageSuggestion}
                >
                    <Grid container gap={2}>
                        <IconWrapper color="neutral">
                            <AutoAwesome />
                        </IconWrapper>
                        <Typography>
                            {intl('sugestInvestimentMsg')}
                        </Typography>
                    </Grid>
                </Button>
            </Grid>
            {propertiesPopup}
        </Accordion.Details>
    )
}

export { Details };

